import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const EMPTY_LINE = { description: "", quantity: 1, unit_price: "", cost_code_id: "", billability: "non_billable", job_id: "" };

/** Vendor credits (Package 38, workflow doc §7.2) — money the vendor owes
 * back, recorded against the original bill's coding and synced to
 * QuickBooks as a VendorCredit. Embedded as a tab in the Invoices page.
 * `prefillInvoiceId` (from ?credit_for=) opens the modal against that bill. */
export default function VendorCreditsView({ prefillInvoiceId = null }) {
  const [credits, setCredits] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null); // {invoice_id, reason, lines: [...]}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/contractor/vendor-credits").then((r) => setCredits(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/contractor/invoices").then((r) => setInvoices(r.data.filter((i) => ["approved", "synced", "paid"].includes(i.status)))).catch(() => {});
    api.get("/contractor/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/contractor/cost-codes").then((r) => setCodes(r.data)).catch(() => {});
  }, []);

  const invoiceById = useMemo(() => Object.fromEntries(invoices.map((i) => [i.id, i])), [invoices]);

  const openModal = (invoiceId) => {
    const inv = invoiceById[invoiceId];
    const lines = inv?.lines?.length
      ? inv.lines.map((l) => ({
          description: l.description || "", quantity: l.quantity || 1, unit_price: l.unit_price ?? "",
          cost_code_id: l.cost_code_id || "", billability: l.billability || "non_billable",
          job_id: l.context?.job_id || "",
        }))
      : [{ ...EMPTY_LINE }];
    setForm({ invoice_id: invoiceId || "", reason: "", lines });
    setCreating(true);
  };

  useEffect(() => {
    if (prefillInvoiceId && invoices.length) openModal(prefillInvoiceId);
  }, [prefillInvoiceId, invoices.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLine = (idx, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const total = form ? form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0) : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        invoice_id: form.invoice_id, reason: form.reason,
        lines: form.lines.map((l) => ({
          description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price),
          cost_code_id: l.cost_code_id, billability: l.billability,
          context: { type: "job", job_id: l.job_id },
        })),
      };
      const r = await api.post("/contractor/vendor-credits", body);
      toast.success(`Vendor credit ${r.data.credit_number} recorded — it syncs to QuickBooks as a VendorCredit.`);
      setCreating(false);
      setForm(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.message || "Check the credit details" : detail || "Could not create the credit", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const syncOne = async (credit) => {
    try {
      const r = await api.post(`/contractor/vendor-credits/${credit.id}/sync`);
      if (r.data.ok) toast.success(`${credit.credit_number} synced to QuickBooks (VendorCredit ${r.data.qbo_id}).`);
      else toast.error(r.data.error || "Sync failed — see Sync Issues.", { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed", { duration: 8000 });
    }
    load();
  };

  const voidOne = async (credit) => {
    if (!window.confirm(`Void ${credit.credit_number}? This removes the credit before it reaches QuickBooks.`)) return;
    try {
      await api.delete(`/contractor/vendor-credits/${credit.id}`);
      toast.success(`${credit.credit_number} voided.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not void the credit", { duration: 8000 });
    }
    load();
  };

  const canSubmit = form && form.invoice_id && form.reason.trim() &&
    form.lines.length > 0 && form.lines.every((l) => l.description && Number(l.quantity) > 0 && Number(l.unit_price) > 0 && l.cost_code_id && l.job_id);

  return (
    <div className="mt-5" data-testid="vendor-credits-view">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-500 max-w-xl">
          Credits the vendor owes back — damaged goods, returns, price adjustments. Each one references the
          original bill and books to the same job and cost code, then syncs to QuickBooks as a vendor credit.
        </p>
        <button onClick={() => openModal(prefillInvoiceId || "")} data-testid="new-vendor-credit"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-bold bg-sky-500 text-white hover:bg-sky-600 shrink-0">
          <Plus size={14} /> New credit
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-4 text-[13px]" data-testid="vendor-credits-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Credit #</th><th className="text-left py-2.5">Vendor</th>
              <th className="text-left py-2.5">Against</th><th className="text-left py-2.5">Reason</th>
              <th className="text-left py-2.5">Date</th><th className="text-right py-2.5">Amount</th>
              <th className="text-left py-2.5 pl-3">Status</th><th className="py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {credits.map((c) => (
              <tr key={c.id} className="border-b border-slate-50" data-testid={`credit-row-${c.credit_number}`}>
                <td className="py-3 mono font-medium">{c.credit_number}</td>
                <td className="py-3 text-slate-600">{c.vendor_name || "—"}</td>
                <td className="py-3 mono text-slate-600">{c.invoice_number || c.po_number || "—"}</td>
                <td className="py-3 text-slate-600 max-w-[260px] truncate" title={c.reason}>{c.reason}</td>
                <td className="py-3 text-slate-600">{fmtDate(c.credit_date)}</td>
                <td className="py-3 text-right mono font-medium text-emerald-600">−{fmt(c.total)}</td>
                <td className="py-3 pl-3">
                  <span className={`ss-pill ${c.status === "synced" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {c.status === "synced" ? "Synced" : "Open"}
                  </span>
                </td>
                <td className="py-3 text-right whitespace-nowrap">
                  {c.status !== "synced" && (
                    <>
                      <button onClick={() => syncOne(c)} data-testid={`sync-credit-${c.credit_number}`}
                        className="px-2.5 py-1 rounded-md text-[12px] font-bold bg-slate-900 text-white hover:bg-slate-700">
                        Sync to QuickBooks
                      </button>
                      <button onClick={() => voidOne(c)} title="Void this credit" data-testid={`void-credit-${c.credit_number}`}
                        className="ml-1.5 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 align-middle">
                        <Undo2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {credits.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400 text-sm">No vendor credits yet — record one when a vendor owes money back on a bill.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setForm(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New vendor credit</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4" data-testid="vendor-credit-form">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Against invoice</label>
                <SearchableSelect value={form.invoice_id} onChange={(e) => openModal(e.target.value)} data-testid="credit-invoice-select"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] bg-white">
                  <option value="">Choose the original bill…</option>
                  {invoices.map((i) => (
                    <option key={i.id} value={i.id}>{i.invoice_number} — {i.vendor_name} ({fmt(i.total)})</option>
                  ))}
                </SearchableSelect>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Reason (goes to the QuickBooks memo)</label>
                <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="credit-reason"
                  placeholder="e.g. 2 bags arrived damaged — vendor credit memo #…"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px]" />
              </div>

              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Credit lines</label>
                {form.lines.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })} placeholder="Description"
                      className="col-span-4 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[12.5px]" />
                    <input type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} placeholder="Qty"
                      className="col-span-1 border border-slate-200 rounded-lg px-2 py-1.5 text-[12.5px] mono" />
                    <input type="number" min="0" step="any" value={l.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} placeholder="$/unit"
                      className="col-span-2 border border-slate-200 rounded-lg px-2 py-1.5 text-[12.5px] mono" />
                    <SearchableSelect value={l.cost_code_id} onChange={(e) => setLine(idx, { cost_code_id: e.target.value })}
                      className="col-span-2 border border-slate-200 rounded-lg px-2 py-1.5 text-[12.5px] bg-white">
                      <option value="">Cost code…</option>
                      {codes.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </SearchableSelect>
                    <SearchableSelect value={l.job_id} onChange={(e) => setLine(idx, { job_id: e.target.value })}
                      className="col-span-2 border border-slate-200 rounded-lg px-2 py-1.5 text-[12.5px] bg-white">
                      <option value="">Job…</option>
                      {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number || j.name}</option>)}
                    </SearchableSelect>
                    <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}
                      className="col-span-1 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 justify-self-center" title="Remove line">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }))}
                  className="text-[12px] font-bold text-sky-600 hover:text-sky-700">+ Add line</button>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="text-[13px] text-slate-500">Credit total: <span className="mono font-bold text-emerald-600">−{fmt(total)}</span></div>
                <button onClick={submit} disabled={!canSubmit || busy} data-testid="submit-vendor-credit"
                  className="px-4 py-2 rounded-lg text-[12.5px] font-bold bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-40">
                  {busy ? "Recording…" : "Record credit"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
